import type { RecognitionPeak } from '../types';
import { harmonicNumberFor } from '../recognition/pitch';
import { midiToHz } from '../util/note';
import type { CalibrationStep } from './types';

export const CALIBRATION_EXPECTED_HARMONIC_MAX = 8;
export const CALIBRATION_EXPECTED_HARMONIC_CENTS = 55;

export interface CalibrationExpectedHarmonicMatch {
  peakIndex: number;
  peak: RecognitionPeak;
  harmonic: number;
}

export interface SingleNoteFrameEvidence {
  coherent: boolean;
  direct: boolean;
  matches: CalibrationExpectedHarmonicMatch[];
  harmonicNumbers: number[];
  nonOctaveHarmonicNumbers: number[];
}

const isOctaveHarmonic = (harmonic: number) => harmonic === 2 || harmonic === 4 || harmonic === 8;

export const expectedHarmonicNumberForStep = (step: CalibrationStep, peakHz: number) => {
  if (!step.expectedMidi || step.expectedMidi.length === 0) return null;

  for (const expectedMidi of step.expectedMidi) {
    const harmonicNumber = harmonicNumberFor(
      peakHz,
      midiToHz(expectedMidi),
      CALIBRATION_EXPECTED_HARMONIC_CENTS,
    );
    if (harmonicNumber != null && harmonicNumber <= CALIBRATION_EXPECTED_HARMONIC_MAX) {
      return harmonicNumber;
    }
  }

  return null;
};

export const analyzeSingleNoteFrameEvidence = (
  step: CalibrationStep,
  peaks: RecognitionPeak[],
): SingleNoteFrameEvidence => {
  const matches = peaks.reduce<CalibrationExpectedHarmonicMatch[]>(
    (currentMatches, peak, peakIndex) => {
      const harmonic = expectedHarmonicNumberForStep(step, peak.hz);
      if (harmonic != null) currentMatches.push({ peakIndex, peak, harmonic });
      return currentMatches;
    },
    [],
  );
  const direct = matches.some((match) => match.harmonic === 1);
  const harmonicNumbers = Array.from(
    new Set(matches.filter((match) => match.harmonic > 1).map((match) => match.harmonic)),
  ).sort((left, right) => left - right);
  const nonOctaveHarmonicNumbers = harmonicNumbers.filter(
    (harmonic) => !isOctaveHarmonic(harmonic),
  );
  const lowestExpectedMidi =
    step.expectedMidi && step.expectedMidi.length > 0 ? Math.min(...step.expectedMidi) : null;
  const coherentHarmonicCluster =
    lowestExpectedMidi != null && lowestExpectedMidi <= 36
      ? harmonicNumbers.length >= 2 && nonOctaveHarmonicNumbers.length >= 1
      : harmonicNumbers.length >= 3 && nonOctaveHarmonicNumbers.length >= 2;

  return {
    coherent: direct || coherentHarmonicCluster,
    direct,
    matches,
    harmonicNumbers,
    nonOctaveHarmonicNumbers,
  };
};
